import { indicationOptions } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheet-labels";
import { Box, Typography } from "@material-ui/core";
import type {
  LungWorksheetData,
  LungZoneFindings,
} from "../../../../global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { EPOCUS_TEXT_SECONDARY, rem } from "../epocus-palette";
import {
  AllNormalToggle,
  BooleanToggle,
  EnumChipSelect,
  IndicationChips,
  NotesField,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "./worksheet-primitives";

const INDICATION_OPTIONS = indicationOptions("lung");

const REQUIRED_VIEW_OPTIONS = [
  { key: "rUpperAnterior", label: "R Upper Anterior" },
  { key: "rLowerAnterior", label: "R Lower Anterior" },
  { key: "lUpperAnterior", label: "L Upper Anterior" },
  { key: "lLowerAnterior", label: "L Lower Anterior" },
] as const;

const OPTIONAL_VIEW_OPTIONS = [
  { key: "rPosterior", label: "R Posterior" },
  { key: "lPosterior", label: "L Posterior" },
] as const;

const B_LINES_OPTIONS = [
  { value: "0", label: "0" },
  { value: "1-2", label: "1-2" },
  { value: "3+", label: "3+" },
  { value: "confluent", label: "Confluent" },
] as const;

const EFFUSION_OPTIONS = [
  { value: "none", label: "None" },
  { value: "small", label: "Small" },
  { value: "moderate", label: "Moderate" },
  { value: "large", label: "Large" },
] as const;

const ZONE_CONFIG = [
  { key: "rUpper", label: "R Upper" },
  { key: "rLower", label: "R Lower" },
  { key: "lUpper", label: "L Upper" },
  { key: "lLower", label: "L Lower" },
] as const;

export const isLungComplete = (data: LungWorksheetData): boolean =>
  ZONE_CONFIG.every(({ key }) => {
    const z = data.zones[key];
    return (
      z.lungSliding !== null &&
      z.bLines !== null &&
      z.pleuralEffusion !== null &&
      z.consolidation !== null
    );
  });

const FindingLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

const ViewGroupLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 8,
    }}
  >
    {children}
  </Typography>
);

const ZoneCard: FC<{
  label: string;
  zone: LungZoneFindings;
  onChange: (zone: LungZoneFindings) => void;
  disabled: boolean;
}> = ({ label, zone, onChange, disabled }) => (
  <WorksheetSection label={label}>
    <FindingLabel>Lung Sliding</FindingLabel>
    <BooleanToggle
      value={zone.lungSliding}
      onChange={(v) => onChange({ ...zone, lungSliding: v })}
      disabled={disabled}
    />

    <FindingLabel>B-Lines</FindingLabel>
    <EnumChipSelect
      options={[...B_LINES_OPTIONS]}
      selected={zone.bLines}
      onChange={(v) =>
        onChange({ ...zone, bLines: v as LungZoneFindings["bLines"] })
      }
      disabled={disabled}
    />

    <FindingLabel>Pleural Effusion</FindingLabel>
    <EnumChipSelect
      options={[...EFFUSION_OPTIONS]}
      selected={zone.pleuralEffusion}
      onChange={(v) =>
        onChange({
          ...zone,
          pleuralEffusion: v as LungZoneFindings["pleuralEffusion"],
        })
      }
      disabled={disabled}
    />

    <FindingLabel>Consolidation</FindingLabel>
    <BooleanToggle
      value={zone.consolidation}
      onChange={(v) => onChange({ ...zone, consolidation: v })}
      disabled={disabled}
    />
  </WorksheetSection>
);

export const LungWorksheet: FC<{
  data: LungWorksheetData;
  onChange: (data: LungWorksheetData) => void;
  isAllNormal: boolean;
  onToggleAllNormal: () => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
}> = ({
  data,
  onChange,
  isAllNormal,
  onToggleAllNormal,
  readOnly,
  imageGallery,
}) => {
  const updateZone = (
    key: keyof LungWorksheetData["zones"],
    zone: LungZoneFindings,
  ) => {
    onChange({ ...data, zones: { ...data.zones, [key]: zone } });
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <AllNormalToggle
        isAllNormal={isAllNormal}
        onToggle={onToggleAllNormal}
        disabled={readOnly}
      />

      <WorksheetColumns>
        <WorksheetColumnLeft>
          <WorksheetSection label="Indication">
            <IndicationChips
              options={[...INDICATION_OPTIONS]}
              selected={data.indication}
              onChange={(v) =>
                onChange({
                  ...data,
                  indication: v as LungWorksheetData["indication"],
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          <WorksheetSection label="Views">
            <ViewGroupLabel>Required</ViewGroupLabel>
            <ViewChecklist
              views={[...REQUIRED_VIEW_OPTIONS]}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({
                  ...data,
                  views: { ...data.views, [key]: checked },
                })
              }
              disabled={readOnly}
            />
            <ViewGroupLabel>Optional</ViewGroupLabel>
            <ViewChecklist
              views={[...OPTIONAL_VIEW_OPTIONS]}
              checked={data.views}
              onChange={(key, checked) =>
                onChange({
                  ...data,
                  views: { ...data.views, [key]: checked },
                })
              }
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnLeft>

        <WorksheetColumnRight>
          {ZONE_CONFIG.map((z) => (
            <ZoneCard
              key={z.key}
              label={z.label}
              zone={data.zones[z.key]}
              onChange={(zone) => updateZone(z.key, zone)}
              disabled={readOnly}
            />
          ))}

          {imageGallery && (
            <WorksheetSection label="Images / Clips">
              {imageGallery}
            </WorksheetSection>
          )}

          <WorksheetSection label="Notes">
            <NotesField
              value={data.notes}
              onChange={(v) => onChange({ ...data, notes: v })}
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnRight>
      </WorksheetColumns>
    </Box>
  );
};
